import isEqual from 'lodash/isEqual';
import { useLayoutEffect } from 'react';

import { useTabContentConfig } from '../components/PageConfigProvider/PageConfigProvider';
import { POSTINGS_PAGE_IDS } from '../components/PageConfigProvider/types';
import type { PositionedItem } from '../components/PageConfigProvider/types/item';
import type { MetricValues } from '../components/PageConfigProvider/types/metric';
import { usePrimaryFilterState } from '../filters/hooks/usePrimaryFilterState';

const POSTINGS_PAGE_ID_SET: ReadonlySet<string> = new Set(POSTINGS_PAGE_IDS);

const isPostingsPage = (id: string) => POSTINGS_PAGE_ID_SET.has(id);

const postingsMetrics: MetricValues[] = [
  'postings_active',
  'postings_new',
  'postings_removed',
];

const getVolumeItemId = (pageId: string) => `${pageId}-postings-volume`;
const getLineItemId = (pageId: string) => `${pageId}-postings-line`;

const createVolumeChartItem = (
  baseItem: PositionedItem,
  pageId: string
): PositionedItem => ({
  ...baseItem,
  id: getVolumeItemId(pageId),
  plot: {
    ...baseItem.plot,
    chartType: 'volume',
    defaultMetricOptions: ['postings_volume'],
  },
});

const createLineChartItem = (
  baseItem: PositionedItem,
  pageId: string
): PositionedItem => ({
  ...baseItem,
  id: getLineItemId(pageId),
  plot: {
    ...baseItem.plot,
    chartType: 'line',
    defaultMetricOptions: postingsMetrics,
  },
});

export const usePostingsVolumeChartToggle = () => {
  const {
    items,
    config,
    dispatch,
    primaryFilter: tabPrimaryFilter,
  } = useTabContentConfig();
  const { primaryFilter, activePrimaryFilterLength } = usePrimaryFilterState(
    tabPrimaryFilter?.id
  );

  const isPrimaryFilterReady = primaryFilter?.id === tabPrimaryFilter?.id;
  const activePrimaryCount = activePrimaryFilterLength;

  useLayoutEffect(() => {
    if (!isPostingsPage(config.id)) return;
    if (!isPrimaryFilterReady || activePrimaryCount < 1) return;

    const volumeItemId = getVolumeItemId(config.id);
    const lineItemId = getLineItemId(config.id);

    // Find the item by checking both possible IDs (volume or line)
    const postingsItem =
      items.find((item) => item.id === volumeItemId) ??
      items.find((item) => item.id === lineItemId);

    if (!postingsItem || !postingsItem.plot) return;

    // Allow manually picking a different chart type
    const currentChartType = postingsItem.plot.chartType;
    if (
      postingsItem.id === volumeItemId &&
      currentChartType !== undefined &&
      currentChartType !== 'volume'
    ) {
      return;
    }

    const isVolumeChart = activePrimaryCount === 1;
    const desiredItemId = isVolumeChart ? volumeItemId : lineItemId;
    const desiredItem = isVolumeChart
      ? createVolumeChartItem(postingsItem, config.id)
      : createLineChartItem(postingsItem, config.id);

    // Check if the item needs to be updated (chart type, metric options, or ID)
    const needsUpdate =
      postingsItem.id !== desiredItemId ||
      postingsItem.plot.chartType !== desiredItem.plot?.chartType ||
      !isEqual(
        postingsItem.plot.defaultMetricOptions,
        desiredItem.plot?.defaultMetricOptions
      );

    if (needsUpdate) {
      // Find the index of the current item to preserve its position
      const currentItemIndex = items.findIndex(
        (item) => item.id === postingsItem.id
      );

      // Remove the old item(s) and insert the new item at the same position
      const filteredItems = items.filter(
        (item) => item.id !== volumeItemId && item.id !== lineItemId
      );
      const insertIndex =
        currentItemIndex >= 0 ? currentItemIndex : filteredItems.length;
      const updatedItems = [
        ...filteredItems.slice(0, insertIndex),
        desiredItem,
        ...filteredItems.slice(insertIndex),
      ];

      if (!isEqual(updatedItems, items)) {
        dispatch({ type: 'SET_ITEMS', payload: updatedItems });
      }
    }
  }, [config.id, items, activePrimaryCount, isPrimaryFilterReady, dispatch]);
};
